import { Activity, AlarmClock, BarChart3, CalendarDays, CheckCircle2, Clock, Filter, Laptop, LogOut, Mail, RefreshCw, Share2, Smartphone, Tablet, Timer, X } from 'lucide-react'
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getUserLoginHistory } from "../../services/action/common"

// Login Details Modal Component
const LoginDetailsModal = ({ isOpen, onClose, userId }) => {
  // State for date filter
  const { userHistory, loading } = useSelector((state) => state.commonReducer)
  const [dateFilter, setDateFilter] = useState("today")
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date().toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  })
  const [activeTab, setActiveTab] = useState("activity")
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [userData, setUserData] = useState(null)

  const dispatch = useDispatch();

  // Fetch data when modal opens or filter changes
  useEffect(() => {
    if (userId && isOpen) {
      fetchUserData();
    }
  }, [userId, isOpen]);

  // Update local state when data is received from Redux
  useEffect(() => {
    if (userHistory) {
      setUserData(userHistory)
    }
  }, [userHistory])

  // Function to fetch user data with current filters
  const fetchUserData = () => {
    dispatch(getUserLoginHistory({
      data: {
        userId,
        filters: {
          dateFilter,
          startDate: customDateRange.start,
          endDate: customDateRange.end
        }
      }
    }))
  }

  // Apply filter and refresh data
  const applyFilter = () => {
    fetchUserData();
    setIsFilterOpen(false)
  }

  // Handle filter change with immediate update for non-custom filters
  const handleFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== "custom") {
      setTimeout(() => {
        dispatch(getUserLoginHistory({
          data: {
            userId,
            filters: {
              dateFilter: filter,
              startDate: customDateRange.start,
              endDate: customDateRange.end
            }
          }
        }))
      }, 100);
    }
  }

  // Get device icon based on device type
  const getDeviceIcon = (device) => {
    if (!device) return <Laptop className="h-4 w-4 text-gray-500" />
    
    switch (device.toLowerCase()) {
      case "mobile":
      case "android":
      case "iphone":
        return <Smartphone className="h-4 w-4 text-gray-500" />
      case "tablet":
      case "ipad":
        return <Tablet className="h-4 w-4 text-gray-500" />
      case "browser":
      case "desktop":
      case "laptop":
      default:
        return <Laptop className="h-4 w-4 text-gray-500" />
    }
  }

  // Calculate statistics based on filtered data
  const calculateStatistics = () => {
    if (!userData || !userData.activityLog || !userData.activityLog.loginHistory) {
      return {
        totalOnlineTime: "0h 0m",
        loginCount: 0,
        logoutCount: 0,
        uniqueDays: 0,
      }
    }

    const loginHistory = userData.activityLog.loginHistory

    // Total online hours (sum of all durations from filtered history)
    let totalOnlineMinutes = 0
    loginHistory.forEach((entry) => {
      if (entry.type === "logout" && entry.duration !== "-") {
        const durationParts = entry.duration.split("h ")
        if (durationParts.length === 2) {
          const hours = Number.parseInt(durationParts[0]) || 0
          const minutes = Number.parseInt(durationParts[1].replace("m", "")) || 0
          totalOnlineMinutes += hours * 60 + minutes
        }
      }
    })

    const onlineHours = Math.floor(totalOnlineMinutes / 60)
    const onlineMinutes = totalOnlineMinutes % 60

    return {
      totalOnlineTime: `${onlineHours}h ${onlineMinutes}m`,
      loginCount: loginHistory.filter((entry) => entry.type === "login").length,
      logoutCount: loginHistory.filter((entry) => entry.type === "logout").length,
      uniqueDays: [...new Set(loginHistory.map((entry) => entry.date))].length,
    }
  }

  const stats = calculateStatistics()

  if (!isOpen) return null

  // Enhanced modal design that's always centered and mobile-friendly
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-0">
      {/* Backdrop with blur effect */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal - increased size and mobile-friendly with responsive sizing */}
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-[850px] max-h-[90vh] overflow-y-auto animate-fadeIn"
        style={{
          animation: "fadeIn 0.3s ease-out",
          zIndex: 51,
        }}
      >
        {/* Header with gradient background */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-b pb-3 px-4 sm:px-6 pt-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            User Activity Dashboard
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`p-1.5 rounded-full hover:bg-white/80 transition-colors ${isFilterOpen ? 'bg-white text-blue-700' : 'text-blue-600'}`}
                title="Filter data"
              >
                <Filter className="h-5 w-5" />
              </button>
              {isFilterOpen && (
                <>
                  {/* Overlay to close filter when clicking outside */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsFilterOpen(false)}
                  ></div>
                  
                  {/* Filter dropdown */}
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg p-4 z-20 border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-800">Filter by date</h4>
                      <button 
                        onClick={() => setIsFilterOpen(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex flex-col gap-2.5">
                        <label className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50">
                          <input
                            type="radio"
                            name="dateFilter"
                            value="today"
                            checked={dateFilter === "today"}
                            onChange={() => handleFilterChange("today")}
                            className="text-blue-600 h-4 w-4"
                          />
                          <span className="text-sm">Today</span>
                        </label>
                        <label className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50">
                          <input
                            type="radio"
                            name="dateFilter"
                            value="yesterday"
                            checked={dateFilter === "yesterday"}
                            onChange={() => handleFilterChange("yesterday")}
                            className="text-blue-600 h-4 w-4"
                          />
                          <span className="text-sm">Yesterday</span>
                        </label>
                        <label className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50">
                          <input
                            type="radio"
                            name="dateFilter"
                            value="last7days"
                            checked={dateFilter === "last7days"}
                            onChange={() => handleFilterChange("last7days")}
                            className="text-blue-600 h-4 w-4"
                          />
                          <span className="text-sm">Last 7 days</span>
                        </label>
                        <label className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50">
                          <input
                            type="radio"
                            name="dateFilter"
                            value="last30days"
                            checked={dateFilter === "last30days"}
                            onChange={() => handleFilterChange("last30days")}
                            className="text-blue-600 h-4 w-4"
                          />
                          <span className="text-sm">Last 30 days</span>
                        </label>
                        <label className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50">
                          <input
                            type="radio"
                            name="dateFilter"
                            value="custom"
                            checked={dateFilter === "custom"}
                            onChange={() => handleFilterChange("custom")}
                            className="text-blue-600 h-4 w-4"
                          />
                          <span className="text-sm">Custom range</span>
                        </label>
                      </div>

                      {dateFilter === "custom" && (
                        <div className="mt-3 border-t pt-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Start date</label>
                              <input
                                type="date"
                                value={customDateRange.start}
                                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                                className="w-full text-sm p-1.5 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">End date</label>
                              <input
                                type="date"
                                value={customDateRange.end}
                                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                                className="w-full text-sm p-1.5 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              />
                            </div>
                          </div>

                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={applyFilter}
                              disabled={loading}
                              className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-1.5"
                            >
                              {loading ? (
                                <>
                                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Applying...</span>
                                </>
                              ) : (
                                <>Apply Filter</>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/80 transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs navigation */}
        <div className="bg-gray-50 px-4 sm:px-6 py-2 border-b flex overflow-x-auto">
          <button
            onClick={() => setActiveTab("activity")}
            className={`px-3 py-2 text-sm font-medium rounded-md mr-2 whitespace-nowrap ${
              activeTab === "activity" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Activity className="w-4 h-4" />
              Activity Log
            </span>
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-3 py-2 text-sm font-medium rounded-md mr-2 whitespace-nowrap ${
              activeTab === "analytics" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" />
              Usage Analytics
            </span>
          </button>
          <button
            onClick={() => setActiveTab("devices")}
            className={`px-3 py-2 text-sm font-medium rounded-md mr-2 whitespace-nowrap ${
              activeTab === "devices" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Laptop className="w-4 h-4" />
              Devices
            </span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <svg className="animate-spin h-8 w-8 text-blue-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-600">Loading user activity data...</p>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            {/* User Profile Header with enhanced styling - mobile responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-gradient-to-r from-gray-50 to-white p-4 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center shadow-inner flex-shrink-0">
                  <span className="text-xl sm:text-2xl font-bold text-blue-600">{userData?.username?.charAt(0) || "U"}</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">{userData?.username || "User"}</h3>
                  <div className="flex items-center gap-2 text-gray-500 mt-1">
                    <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate max-w-[200px] sm:max-w-none">
                      {userData?.email || "user@example.com"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2 sm:mt-0 text-left sm:text-right">
                <div className="text-xs sm:text-sm text-gray-500">Last Login</div>
                <div className="text-sm sm:text-base font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-lg mt-1 inline-block">
                  {userData?.lastLogin ? new Date(userData.lastLogin).toLocaleString() : "N/A"}
                </div>
              </div>
            </div>

            {/* Date filter indicator */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CalendarDays className="h-4 w-4 text-blue-600" />
                <span>
                  {dateFilter === "today" && "Showing data for today"}
                  {dateFilter === "yesterday" && "Showing data for yesterday"}
                  {dateFilter === "last7days" && "Showing data for last 7 days"}
                  {dateFilter === "last30days" && "Showing data for last 30 days"}
                  {dateFilter === "custom" && `Showing data from ${customDateRange.start} to ${customDateRange.end}`}
                </span>
              </div>
              <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                {stats.uniqueDays} day{stats.uniqueDays !== 1 ? "s" : ""} • {userData?.activityLog?.loginHistory?.length || 0} events
              </div>
            </div>

            {activeTab === "activity" && (
              <>
                {/* Summary Stats Cards - enhanced with gradients and animations */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  {/* Online Hours Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4 transition-all hover:shadow-md hover:translate-y-[-2px] duration-300">
                    <div className="flex items-center gap-3 mb-1 sm:mb-2">
                      <div className="bg-white p-1.5 sm:p-2 rounded-full shadow-sm">
                        <Timer className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      </div>
                      <h4 className="text-sm sm:text-base font-medium text-blue-700">Online Hours</h4>
                    </div>
                    <p className="text-sm sm:text-base pl-8 sm:pl-10 text-gray-700 font-medium">
                      {userData?.activityLog?.todayAcitivity?.onlineHours || "0h 0m"}
                    </p>
                  </div>

                  {/* Login Count Card */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 sm:p-4 transition-all hover:shadow-md hover:translate-y-[-2px] duration-300">
                    <div className="flex items-center gap-3 mb-1 sm:mb-2">
                      <div className="bg-white p-1.5 sm:p-2 rounded-full shadow-sm">
                        <LogOut className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 rotate-180" />
                      </div>
                      <h4 className="text-sm sm:text-base font-medium text-green-700">Login Count</h4>
                    </div>
                    <p className="text-sm sm:text-base pl-8 sm:pl-10 text-gray-700 font-medium">
                      {userData?.activityLog?.todayAcitivity?.loginCount || "0"} times
                    </p>
                  </div>

                  {/* Logout Count Card */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 sm:p-4 transition-all hover:shadow-md hover:translate-y-[-2px] duration-300">
                    <div className="flex items-center gap-3 mb-1 sm:mb-2">
                      <div className="bg-white p-1.5 sm:p-2 rounded-full shadow-sm">
                        <LogOut className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                      </div>
                      <h4 className="text-sm sm:text-base font-medium text-purple-700">Logout Count</h4>
                    </div>
                    <p className="text-sm sm:text-base pl-8 sm:pl-10 text-gray-700 font-medium">
                      {userData?.activityLog?.todayAcitivity?.logoutCount || "0"} times
                    </p>
                  </div>

                  {/* Device Type Card */}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 sm:p-4 transition-all hover:shadow-md hover:translate-y-[-2px] duration-300">
                    <div className="flex items-center gap-3 mb-1 sm:mb-2">
                      <div className="bg-white p-1.5 sm:p-2 rounded-full shadow-sm">
                        {getDeviceIcon(userData?.activityLog?.todayAcitivity?.primaryDevice)}
                      </div>
                      <h4 className="text-sm sm:text-base font-medium text-amber-700">Primary Device</h4>
                    </div>
                    <p className="text-sm sm:text-base pl-8 sm:pl-10 text-gray-700 font-medium">
                      {userData?.activityLog?.todayAcitivity?.primaryDevice || "Unknown"}
                    </p>
                  </div>
                </div>

                {/* Login History Timeline - enhanced with better spacing and visual indicators */}
                <div className="bg-gradient-to-br from-gray-50 to-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center mb-3 sm:mb-4 pb-2 border-b">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
                      <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      Login History
                    </h4>
                    <div className="flex items-center gap-2">
                      <button 
                        className="text-xs text-blue-600 flex items-center gap-1 hover:underline"
                        onClick={fetchUserData}
                        disabled={loading}
                      >
                        <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      <button className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
                        <Share2 className="h-3 w-3" />
                        Export
                      </button>
                    </div>
                  </div>

                  {/* Table header */}
                  <div className="hidden sm:grid grid-cols-5 text-xs font-medium text-gray-500 uppercase mb-2 px-2">
                    <div>Type</div>
                    <div>Date & Time</div>
                    <div>Device</div>
                    <div>Browser/OS</div>
                    <div>Duration</div>
                  </div>

                  <div className="space-y-3 sm:space-y-2">
                    {userData?.activityLog?.loginHistory && userData.activityLog.loginHistory.length > 0 ? (
                      userData.activityLog.loginHistory.map((entry, index) => (
                        <div key={index} className="flex items-start group">
                          <div className="relative mr-2 sm:mr-4 hidden sm:block">
                            {index < userData.activityLog.loginHistory.length - 1 && (
                              <div className="h-full w-0.5 bg-gray-200 absolute left-2 top-5 bottom-0 group-hover:bg-blue-200 transition-colors duration-300"></div>
                            )}
                            <div
                              className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${
                                entry.type === "login"
                                  ? "bg-gradient-to-r from-green-400 to-green-500"
                                  : "bg-gradient-to-r from-red-400 to-red-500"
                              } border-2 border-white shadow-sm group-hover:scale-110 transition-transform duration-300`}
                            ></div>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center w-full bg-white p-2 sm:p-3 rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300 border border-gray-100">
                            {/* Mobile view */}
                            <div className="sm:hidden space-y-2 w-full">
                              <div className="flex justify-between items-center w-full">
                                <div className="text-xs font-medium text-gray-800 capitalize flex items-center gap-1.5">
                                  <div
                                    className={`w-3 h-3 rounded-full ${
                                      entry.type === "login" ? "bg-green-500" : "bg-red-500"
                                    } mr-1`}
                                  ></div>
                                  {entry.type === "login" ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                  ) : (
                                    <LogOut className="h-3.5 w-3.5 text-red-500" />
                                  )}
                                  {entry.type}
                                </div>
                                <div className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                  {entry.actionTime}
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <div className="text-gray-500">{entry.date}</div>
                                <div className="text-gray-700 font-medium">{entry.duration}</div>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 p-1.5 rounded-md">
                                {getDeviceIcon(entry.device)}
                                <span className="truncate">{entry.deviceModel}</span>
                              </div>
                            </div>

                            {/* Desktop view */}
                            <div className="hidden sm:grid sm:grid-cols-5 sm:w-full sm:items-center text-sm">
                              <div className="flex items-center gap-1.5">
                                {entry.type === "login" ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <LogOut className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                                <span className="capitalize font-medium">{entry.type}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-gray-700">{entry.actionTime}</span>
                                <span className="text-xs text-gray-500">{entry.date}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {getDeviceIcon(entry.device)}
                                <span>{entry.device}</span>
                              </div>
                              <div className="text-gray-600 truncate">{entry.deviceModel}</div>
                              <div className="font-medium text-gray-700">{entry.duration}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No login history found for the selected date range
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === "analytics" && (
              <div className="space-y-6">
                {/* Usage Analytics */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2 pb-2 border-b">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Usage Analytics
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left column - Time metrics */}
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Average Login Time</h5>
                          <Clock className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {userData?.activityLog?.usageAnalytics?.averageLoginTime || "N/A"}
                        </p>
                        <div className="mt-1 text-xs text-gray-500">Based on last 30 days of activity</div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Average Logout Time</h5>
                          <LogOut className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {userData?.activityLog?.usageAnalytics?.averageLogoutTime || "N/A"}
                        </p>
                        <div className="mt-1 text-xs text-gray-500">Based on last 30 days of activity</div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Login Frequency</h5>
                          <Activity className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {userData?.activityLog?.usageAnalytics?.loginFrequency || "0 times/week"}
                        </p>
                        <div className="mt-1 text-xs text-gray-500">Average number of logins per week</div>
                      </div>
                    </div>

                    {/* Right column - Activity patterns */}
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Most Active Day</h5>
                          <CalendarDays className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {userData?.activityLog?.usageAnalytics?.mostActiveDay || "N/A"}
                        </p>
                        <div className="mt-1 text-xs text-gray-500">Day with highest login activity</div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Most Active Hour</h5>
                          <AlarmClock className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {userData?.activityLog?.usageAnalytics?.mostActiveHour || "N/A"}
                        </p>
                        <div className="mt-1 text-xs text-gray-500">Hour with highest activity</div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Total Online Hours</h5>
                          <Timer className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-gray-800">
                            {userData?.activityLog?.usageAnalytics?.totalOnlineHours?.hours || "0h 0m"}
                          </p>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                            {userData?.activityLog?.usageAnalytics?.totalOnlineHours?.message || "0% of work hours"}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">For the selected period</div>
                      </div>
                    </div>
                  </div>

                  {/* Inactivity Periods */}
                  <div className="mt-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Inactivity Periods</h5>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-500 mb-2">
                        <div>Start Time</div>
                        <div>End Time</div>
                        <div>Duration</div>
                      </div>
                      {userData?.activityLog?.usageAnalytics?.inactivityPeriods && 
                       userData.activityLog.usageAnalytics.inactivityPeriods.length > 0 ? (
                        userData.activityLog.usageAnalytics.inactivityPeriods.map((period, index) => (
                          <div key={index} className="grid grid-cols-3 gap-2 text-sm py-2 border-t border-gray-100">
                            <div>{period.start}</div>
                            <div>{period.end}</div>
                            <div className="font-medium">{period.duration}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">No inactivity periods found</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "devices" && (
              <div className="space-y-6">
                {/* Devices Overview */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2 pb-2 border-b">
                    <Laptop className="h-5 w-5 text-blue-600" />
                    Devices Overview
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 flex items-center gap-4">
                      <div className="bg-white p-3 rounded-full shadow-sm">
                        <Laptop className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm text-blue-700">Desktop/Laptop</div>
                        <div className="text-lg font-semibold">
                          {userData?.activityLog?.devices?.desktopLaptop || "0%"}
                        </div>
                        <div className="text-xs text-gray-600">Primary device</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 flex items-center gap-4">
                      <div className="bg-white p-3 rounded-full shadow-sm">
                        <Smartphone className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-sm text-purple-700">Mobile</div>
                        <div className="text-lg font-semibold">
                          {userData?.activityLog?.devices?.mobile || "0%"}
                        </div>
                        <div className="text-xs text-gray-600">Secondary device</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 flex items-center gap-4">
                      <div className="bg-white p-3 rounded-full shadow-sm">
                        <Tablet className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-sm text-amber-700">Tablet</div>
                        <div className="text-lg font-semibold">
                          {userData?.activityLog?.devices?.tablet || "0%"}
                        </div>
                        <div className="text-xs text-gray-600">Occasional use</div>
                      </div>
                    </div>
                  </div>

                  {/* Device History Table */}
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Device Login History</h5>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="grid grid-cols-4 gap-2 text-xs font-medium text-gray-500 mb-2">
                      <div>Device</div>
                      <div>Browser</div>
                      <div>Operating System</div>
                      <div>Last Used</div>
                    </div>
                    <div className="space-y-2">
                      {userData?.activityLog?.devices?.deviceLoginHistory && 
                       userData.activityLog.devices.deviceLoginHistory.length > 0 ? (
                        userData.activityLog.devices.deviceLoginHistory.map((device, index) => (
                          <div key={index} className="grid grid-cols-4 gap-2 text-sm py-2 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              {getDeviceIcon(device.device)}
                              <span>{device.device}</span>
                            </div>
                            <div>{device.browser || "Unknown"}</div>
                            <div>{device.os || "Unknown"}</div>
                            <div className="font-medium">{device.lastUsed}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">No device history found</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LoginDetailsModal;